import React, { useEffect, useState } from "react";
import { IonRow, IonCol, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonIcon } from "@ionic/react";
import { Horse, Event, EventClass, EventDivision, ScheduleItem, EventEntry, EventClassEntry, Rider } from "../../models";
import { getEventClassesByEventIdWithClassesAndEventDivisions } from "../../utilities/eventClass/EventClass";
import { getEventClassTypeAbbreviation } from "../../utilities/eventClass/EventClassTypes";
import { getEventDivisionById } from "../../utilities/eventDivision/EventDivision";
import { ClassInfoCardsSkeleton } from "../Skeletons/EntrySectionSkeletons";
import SelectedClasses from "./SelectedClasses";
import SelectClassOrDivisionSection from "./SelectClassOrDivisionSection";
import DisciplineCard from "./DisciplineCard";
import { cart } from "ionicons/icons";
import { getScheduleItemsByEventId } from "../../utilities/scheduleItem/ScheduleItem";
import moment from "moment";
import { getEventClassEntriesByEventIdEntryId, getEventClassEntriesWithEventClassByEventEntryId } from "../../utilities/eventClassEntry/EventClassEntry";
import { CreateEntryInterface } from "../../utilities/eventEntry/CreateEventEntryUtils";
import EntryFormSectionHeader, { ENTRY_FORM_SECTION_CONTENT_GAP_PX } from "../Entry/EntryForm/EntryFormSectionHeader";

interface Props {
    event: Event;
    entryInfo: CreateEntryInterface;
    currentEventEntry?: EventEntry;
    selectedHorse?: Horse | null | undefined;
    onSelectedDisciplineData: (data: OptionGroupsSortedByDiscipline[]) => void;
}

export interface formattedOption {
    isExpanded: boolean;
    isChecked: boolean;
    isDisabled: boolean;
    isDivisionEntryOnly: boolean;
    isClassEntryOnly: boolean;
    selectedRider?: (Rider | null | undefined)
    entryFee: string;
    name: string;
    number?: string;
    discipline: string;
    classType?: string;
    classDate?: string; //classDate will only be a string for class. Divisions will have null for this field
    note?: string;
    value: string;
    object: any;
}
export interface formattedOptionGroup {
    division?: formattedOption;
    classes?: formattedOption[];
    class?: formattedOption;
}

export interface OptionGroupsSortedByDiscipline {
    disciplineName: string;
    includesOneOrMoreSelectedClasses: boolean;
    optionGroupArray: formattedOptionGroup[];
}

/** Parse a class/division fee string from the entry form into a dollar amount. */
export function parseFeeAmount(value?: string | number | null): number {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = typeof value === "number" ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * True when this option group is billed as a whole division (same rule as invoice class-fee totals):
 * the division is selected and has a fee greater than 0.
 */
export function isBilledAsDivision(optionGroup: formattedOptionGroup): boolean {
    return Boolean(optionGroup.division?.isChecked && parseFeeAmount(optionGroup.division?.entryFee) > 0);
}

/**
 * Selected-classes total using the same billing rules as `getFormattedEntryClassList`:
 * division fee once when entered by division, otherwise the sum of checked class fees.
 */
export function sumSelectedClassFees(disciplines?: OptionGroupsSortedByDiscipline[] | null): number {
    if (!disciplines?.length) return 0;
    let total = 0;
    for (const discipline of disciplines) {
        for (const optionGroup of discipline.optionGroupArray) {
            if (optionGroup.division && optionGroup.classes) {
                if (isBilledAsDivision(optionGroup)) {
                    total += parseFeeAmount(optionGroup.division.entryFee);
                } else {
                    for (const classItem of optionGroup.classes) {
                        if (classItem.isChecked) {
                            total += parseFeeAmount(classItem.entryFee);
                        }
                    }
                }
            } else if (optionGroup.class?.isChecked) {
                total += parseFeeAmount(optionGroup.class.entryFee || optionGroup.class.object?.entryFee);
            }
        }
    }
    return total;
}

interface CardData {
    title: "Selected Classes" | "Search All Classes" | "Class List";
    content: JSX.Element;
}

const ClassInfoSection: React.FC<Props> = ({ event, entryInfo, currentEventEntry, selectedHorse, onSelectedDisciplineData }) => {
    const [eventClasses, setEventClasses] = useState<EventClass[] | undefined | null>();
    const [selectedEventClasses, setSelectedEventClasses] = useState<EventClass[] | null | undefined>();
    const [disciplinesWithSelectedValues, setDisciplinesWithSelectedValues] = useState<OptionGroupsSortedByDiscipline[] | undefined | null>();
    const [selectedClassesCost, setSelectedClassesCost] = useState<number>(0);
    const [searchText, setSearchText] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        getData(event, currentEventEntry);
    }, [event, currentEventEntry]);

    useEffect(() => {
        setSelectedClassesCost(sumSelectedClassFees(disciplinesWithSelectedValues));
    }, [disciplinesWithSelectedValues]);

    const getData = async (event?: Event, currentEventEntry?: EventEntry) => {
        setIsLoading(true);

        let foundEventClassEntries: EventClassEntry[] = [];
        if (currentEventEntry) {
            foundEventClassEntries = await getEntryInfo(currentEventEntry);
        }

        let foundEventClassesThatEntryHasEntered: EventClass[] = [];
        if (foundEventClassEntries && foundEventClassEntries.length) {
            for (let i = 0; i < foundEventClassEntries.length; i++) {
                const eventClassEntry: EventClassEntry = foundEventClassEntries[i];
                if (eventClassEntry.eventClass) foundEventClassesThatEntryHasEntered.push(eventClassEntry.eventClass);
            }
            setSelectedEventClasses(foundEventClassesThatEntryHasEntered); 
        }

        let foundAllEventClasses: EventClass[] = [];
        let foundAllScheduleItems: ScheduleItem[] = [];
        if (event && (!eventClasses || eventClasses.length === 0)) {
            foundAllEventClasses = await getEventClasses(event.id);
            setEventClasses(foundAllEventClasses);
            foundAllScheduleItems = await getEventScheduleItems(event.id);
        }

        if (event && (!eventClasses || eventClasses.length === 0)) {
            foundAllEventClasses = await getEventClasses(event.id);
            setEventClasses(foundAllEventClasses);
        }

        await formatClassData(foundAllScheduleItems, foundAllEventClasses, foundEventClassesThatEntryHasEntered, foundEventClassEntries);

        setIsLoading(false);
    }

    const getEntryInfo = async (entry: EventEntry) => {
        const eventClassEntriesResult = await getEventClassEntriesWithEventClassByEventEntryId(entry.id);
        if (eventClassEntriesResult.isSuccess) {
            const eventClassEntries: [] = eventClassEntriesResult.result;
            return eventClassEntries;
        } else {
            return [];
        }
    }

    async function getEventClasses(eventId: string) {
        const queryEventClassesResult = await getEventClassesByEventIdWithClassesAndEventDivisions(eventId);
        if (queryEventClassesResult.isSuccess) {
            const allEventClasses = queryEventClassesResult.result;
            return allEventClasses;
        }
        return [];
    }

    async function getEventScheduleItems(eventId: string) {
        const queryScheduleResult = await getScheduleItemsByEventId(eventId);
        if (queryScheduleResult.isSuccess) {
            const scheduleItems: ScheduleItem[] = queryScheduleResult.result;
            return scheduleItems;
        }
        return [];
    }

    const findClassInList = (eventClass: EventClass, eventClassList?: EventClass[]) => {
        let result = false;
        if (eventClassList) {
            for (var i = 0; i < eventClassList.length; i++) {
                const currentEventClass = eventClassList[i];
                if (currentEventClass && currentEventClass.id) {
                    if (eventClass.id === eventClassList[i].id) {
                        result = true;
                    }
                }
            }
        }
        return result;
    };

    const findSelectedRider = (eventClass: EventClass, eventClassEntryList?: EventClassEntry[]) => {
        let result: Rider | undefined | null = undefined;
        if (eventClassEntryList) {
            for (var i = 0; i < eventClassEntryList.length; i++) {
                const currentEventClassEntry: EventClassEntry = eventClassEntryList[i];
                const currentEventClass = currentEventClassEntry.eventClass;
                if (currentEventClass && currentEventClass.id) {
                    if (eventClass.id === currentEventClass.id) {
                        result = currentEventClassEntry.rider;
                    }
                }
            }
        }
        return result;
    };

    const formatClassData = async (scheduleItems: ScheduleItem[], eventClasses: EventClass[], selectedClassList?: EventClass[], foundEventClassEntries?: EventClassEntry[]) => {
        // optionMap = a way of sorting the divisions and classes so that all classes in a division are grouped together
        // Key = eventDivisionId OR eventClassId
        // Value = formattedOptionGroup
        let optionMap = new Map<string, formattedOptionGroup>();

        // disciplinesMap = a way to group the formattedOptionGroups by similar discipline
        // Key = name of discipline
        // Value = formattedOptionGroup
        let disciplinesMap = new Map<string, formattedOptionGroup[]>();

        // currentClassList is the list of EventClassEntries that are for the current entry
        const currentClassList = selectedClassList || selectedEventClasses;

        // Iterate over eventClasses (main loop)
        for (var i = 0; i < eventClasses.length; i++) {
            // Get the current class and it's label info
            const currentClass: EventClass = eventClasses[i];
            // Get the class type info (i.e. over fences, under saddle, eq on the flat, classic, etc.)
            const classType = currentClass.class && currentClass.class.type ? getEventClassTypeAbbreviation(currentClass.class.type) : "";

            // Find the corresponding schedule item:
            let formattedDate: string | undefined = undefined; // Initialize formattedDate for this class
            const currentSchedule = scheduleItems.find((scheduleItem) => scheduleItem.eventClass?.classId === currentClass.class?.id);

            // When schedule item is found for the currentClass format the date, otherwise skip this step
            if (currentSchedule) {
                formattedDate = currentSchedule.eventDay && currentSchedule.eventDay.awsdate ? moment(currentSchedule.eventDay.awsdate).format("ddd MMM Do") : "";
            }

            // Check for any division info connected to this class - if there is division info, this class will need to be shown with the other classes in the division
            let currentDivision: EventDivision | undefined = undefined;
            const currentDivisionId = currentClass.eventDivision?.id;
            if (currentDivisionId) {
                if (currentClass.eventDivision) {
                    currentDivision = currentClass.eventDivision;
                } else {
                    const queryResult = await getEventDivisionById(currentDivisionId);
                    if (queryResult.isSuccess) {
                        currentDivision = queryResult.result;
                    }
                }
            }

            if (currentDivision) {
                // Format with Division on top and then classes listed beneath

                // Check the rules of how users can enter the class and/or division
                let canEnterByDivision = !!currentDivision.entryFee && parseFloat(currentDivision.entryFee) > 0;
                let canEnterByClass = !!currentClass.entryFee && parseFloat(currentClass.entryFee) > 0;

                // TO DO - add the logic back in for an entry that has already been saved.
                const entryIsEnrolledInDivision = currentEventEntry?.divisionList?.includes(currentDivision.id);

                // Check to see if this division is already in the OptionMap
                const result: formattedOptionGroup | null | undefined = optionMap.get(currentDivision.id);
                if (result) {
                    // Simply update the classes list in the option group
                    const currentClassArray = result.classes || [];
                    const isChecked = findClassInList(currentClass, (currentClassList || undefined))
                    const formattedClass: formattedOption = {
                        isExpanded: false,
                        isChecked: isChecked,
                        isDisabled: canEnterByDivision && !canEnterByClass ? true : !!entryIsEnrolledInDivision,
                        isDivisionEntryOnly: canEnterByDivision && !canEnterByClass,
                        isClassEntryOnly: !canEnterByDivision && canEnterByClass,
                        selectedRider: findSelectedRider(currentClass, foundEventClassEntries),
                        entryFee: currentClass?.entryFee || "",
                        name: currentClass.name,
                        number: currentClass.number ? currentClass.number.toString() : "",
                        discipline: currentClass.class?.discipline || "",
                        classType: classType,
                        classDate: formattedDate ?? "",
                        note: "",
                        value: currentClass?.id,
                        object: currentClass,
                    };
                    let updatedClassArray = currentClassArray.concat([formattedClass]);
                    const updatedFormattedOptionGroup: formattedOptionGroup = {
                        ...result,
                        classes: updatedClassArray,
                    };
                    optionMap.set(currentDivision.id, updatedFormattedOptionGroup);
                } else {
                    // Need to format the division info and add it to the OptionMap
                    const formattedDivision: formattedOption = {
                        isExpanded: false,
                        isChecked: !!entryIsEnrolledInDivision,
                        isDisabled: false,
                        isDivisionEntryOnly: canEnterByDivision && !canEnterByClass,
                        isClassEntryOnly: !canEnterByDivision && canEnterByClass,
                        entryFee: currentDivision?.entryFee || "",
                        name: currentDivision.name,
                        number: currentDivision.number ? currentDivision.number.toString() : "",
                        discipline: currentDivision.division?.discipline || "",
                        classType: "",
                        classDate: formattedDate ?? "",
                        note: "(enter entire division or choose classes in this division)",
                        value: currentDivision?.id,
                        object: currentDivision,
                    };

                    const isChecked = findClassInList(currentClass, (currentClassList || undefined));
                    const formattedClass: formattedOption = {
                        isExpanded: false,
                        isChecked: isChecked,
                        isDisabled: canEnterByDivision && !canEnterByClass ? true : !!entryIsEnrolledInDivision,
                        isDivisionEntryOnly: canEnterByDivision && !canEnterByClass,
                        isClassEntryOnly: !canEnterByDivision && canEnterByClass,
                        selectedRider: findSelectedRider(currentClass, foundEventClassEntries),
                        entryFee: currentClass?.entryFee || "",
                        name: currentClass.name,
                        number: currentClass.number ? currentClass.number.toString() : "",
                        discipline: currentClass.class?.discipline || "",
                        classType: classType,
                        classDate: formattedDate ?? "",
                        note: "",
                        value: currentClass?.id,
                        object: currentClass,
                    };

                    const newFormattedOptionGroup: formattedOptionGroup = {
                        division: formattedDivision,
                        classes: [formattedClass],
                    };

                    optionMap.set(currentDivision.id, newFormattedOptionGroup);
                }
            } else {
                // There is no division for the class - just display the class
                const classType = currentClass.class && currentClass.class.type ? getEventClassTypeAbbreviation(currentClass.class.type) : "";
                const isChecked = findClassInList(currentClass, (currentClassList || undefined));
                const formattedClass: formattedOption = {
                    isExpanded: false,
                    isChecked: isChecked,
                    isDisabled: false,
                    isDivisionEntryOnly: false,
                    isClassEntryOnly: true,
                    selectedRider: findSelectedRider(currentClass, foundEventClassEntries),
                    entryFee: currentClass?.entryFee || "",
                    name: currentClass.name,
                    number: currentClass.number ? currentClass.number.toString() : "",
                    discipline: currentClass.class?.discipline || "",
                    classType: classType,
                    note: "",
                    value: currentClass?.id,
                    object: currentClass,
                };
                const newFormattedOptionGroup: formattedOptionGroup = {
                    class: formattedClass,
                };
                optionMap.set(currentClass.id, newFormattedOptionGroup);
            }
        }

        // Loop through each option group, and sort them by discipline
        optionMap.forEach((formattedOptionGroup) => {
            let discipline = "other";
            if (formattedOptionGroup?.division?.discipline) discipline = formattedOptionGroup.division.discipline;
            else if (formattedOptionGroup?.class?.discipline) discipline = formattedOptionGroup.class.discipline;

            // Check if the disciplinesMap already has this discipline
            const disciplineResult: formattedOptionGroup[] | null | undefined = disciplinesMap.get(discipline.toLocaleLowerCase().trim());
            if (disciplineResult) {
                // This discipline is already there, so update its array
                const updatedDisciplineArray = disciplineResult.concat([formattedOptionGroup]);
                disciplinesMap.set(discipline.toLocaleLowerCase().trim(), updatedDisciplineArray);
            } else {
                // Need to add this as a new array for the new discipline
                disciplinesMap.set(discipline.toLocaleLowerCase().trim(), [formattedOptionGroup]);
            }
        });

        // Then loop through the discipline map and format the data into an array
        let disciplinesArray: OptionGroupsSortedByDiscipline[] = [];
        disciplinesMap.forEach((element, key) => {
            const newOption: OptionGroupsSortedByDiscipline = {
                disciplineName: key,
                includesOneOrMoreSelectedClasses: checkIfAtLeastOneClassIsSelected(element), //TO DO: try to make this more efficient
                optionGroupArray: element,
            };
            disciplinesArray.push(newOption);
        });

        const sortedArray = disciplinesArray.sort((a, b) => a.disciplineName.localeCompare(b.disciplineName));

        setDisciplinesWithSelectedValues(sortedArray);
    };

    const checkIfAtLeastOneClassIsSelected = (formattedOptionGroups: formattedOptionGroup[]) => {
        for (let i = 0; i < formattedOptionGroups.length; i++) {
            const current = formattedOptionGroups[i];
            if (current.class) {
                if (current.class.isChecked) return true;
            } else if (current.classes) {
                for (let j = 0; j < current.classes.length; j++) {
                    const currentClass = current.classes[j];
                    if (currentClass.isChecked) return true;
                }
            }
        }
        return false;
    };

    const handleDisciplineFormattedOptionGroupsChange = (index: number, formattedOptionGroups: formattedOptionGroup[]) => {
        if (disciplinesWithSelectedValues) {
            const foundDiscipline = disciplinesWithSelectedValues[index];
            const updatedOptionGroupsSortedByDiscipline: OptionGroupsSortedByDiscipline = {
                ...foundDiscipline,
                includesOneOrMoreSelectedClasses: checkIfAtLeastOneClassIsSelected(formattedOptionGroups),
                optionGroupArray: formattedOptionGroups,
            };
            const updatedDisciplinesArray: OptionGroupsSortedByDiscipline[] = [
                ...disciplinesWithSelectedValues.slice(0, index),
                updatedOptionGroupsSortedByDiscipline,
                ...disciplinesWithSelectedValues.slice(index + 1),
            ];
            setDisciplinesWithSelectedValues(() => updatedDisciplinesArray);
            onSelectedDisciplineData(updatedDisciplinesArray);
        } else {
            console.log("No disciplines were found.");
        }
    };

    const handleChangeSearchText = (input: string) => {
        setSearchText(input);
    };

    const cardsData: CardData[] = [
        {
            title: "Class List",
            content: (
                <>
                    <IonRow>
                        <IonCol className="p-0 m-0">
                            <SelectClassOrDivisionSection onChangeSearchText={handleChangeSearchText} />
                        </IonCol>
                    </IonRow>
                    <IonRow className="mt-2">
                        <IonCol className="p-0 m-0">
                            {disciplinesWithSelectedValues && disciplinesWithSelectedValues.length > 0 ? (
                                <>
                                    {disciplinesWithSelectedValues.map((d, index) => (
                                        <span key={index}>
                                            <DisciplineCard
                                                title={d.disciplineName}
                                                classList={d.optionGroupArray}
                                                searchText={searchText}
                                                defaultRider={entryInfo.currentRider}
                                                onFormattedOptionGroupsChange={(value: formattedOptionGroup[]) => handleDisciplineFormattedOptionGroupsChange(index, value)}
                                            />
                                        </span>
                                    ))}
                                </>
                            ) : (
                                <>
                                    <p>No classes found.</p>
                                </>
                            )}
                        </IonCol>
                    </IonRow>
                </>
            ),
        },
        {
            title: "Selected Classes",
            content: (
                <IonRow>
                    <IonCol className="p-0 m-0">
                        <SelectedClasses
                            disciplineDataArray={disciplinesWithSelectedValues}
                            selectedClassesCost={selectedClassesCost}
                            entryInfo={entryInfo}
                            onClassRemoval={(index: number, value: formattedOptionGroup[]) => handleDisciplineFormattedOptionGroupsChange(index, value)}
                            onRiderUpdate={(updatedDisciplinesWithSelectedValues: OptionGroupsSortedByDiscipline[]) => {
                                setDisciplinesWithSelectedValues(updatedDisciplinesWithSelectedValues);
                                onSelectedDisciplineData(updatedDisciplinesWithSelectedValues);
                            }}
                        />
                    </IonCol>
                </IonRow>
            ),
        }
    ];
    return (
        <>
            <EntryFormSectionHeader
                title={`Enter Classes${selectedHorse ? ` | ${selectedHorse.name}` : ""}`}
                emoji="🏅"
            />

            {isLoading ? (
                <ClassInfoCardsSkeleton />
            ) : (
                <>
                    {cardsData.map((card) => (
                        <IonCard
                            key={card.title}
                            className="rounded-3 p-0 mx-0 bg-white"
                            style={{ overflow: "visible", marginTop: 0, marginBottom: ENTRY_FORM_SECTION_CONTENT_GAP_PX }}>
                            <IonCardHeader className="px-2 py-0">
                                <IonRow className="justify-content-between">
                                    <IonCol sizeXs="11" className="px-0 pb-0">
                                        <IonCardTitle className="font-weight-bold text-primary mt-1">{card.title}</IonCardTitle>
                                    </IonCol>
                                    {card.title == "Selected Classes" && (
                                        <IonCol sizeXs="1" className="pb-0 pl-0 ion-text-right">
                                            <IonIcon className="text-primary mt-1 card-icon-size-2" icon={cart} />
                                        </IonCol>
                                    )}
                                </IonRow>
                            </IonCardHeader>
                            <IonCardContent className="px-2 pb-2">{card.content}</IonCardContent>
                        </IonCard>
                    ))}
                </>
            )}
        </>
    );
};

export default ClassInfoSection;
